import { useEffect, useState } from 'react';

import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useEventInfo } from '@shared/utils/eventInfo';
import { getConfig } from '@config';

/** Joy AI Planner owns the whole admin experience for an event, keyed by event id (not handle). */
export const buildJoyPlannerEventPath = (eventId: string): string => `/${getConfig().joyPlannerBasePath}/e/${eventId}`;

/**
 * Suppresses the redirect for the rest of the session, so the planner can embed joy-web admin pages
 * (iframes, popouts) without them bouncing straight back out to the planner.
 *
 * Append `?disableJoyPlannerRedirect=true` to the embedded URL.
 */
export const JOY_PLANNER_REDIRECT_OVERRIDE_PARAM = 'disableJoyPlannerRedirect';

/**
 * Redirects the joy-web admin surface to the Joy AI Planner frontend for events carrying the
 * `useJoyAIPlanner` event feature flag.
 *
 * Admin renders as normal until the flag and event id resolve, then the redirect fires — so a
 * flagged event may paint admin briefly on the way out. Nothing blocks on this hook, which means
 * events without the flag are completely unaffected.
 *
 * Both inputs ride along on the app-wide `BasicEventInfo` query that `EventInfoProvider` already
 * issues on every admin load, so this adds no network request.
 *
 * Two escape hatches, for different jobs:
 * - `?disableJoyPlannerRedirect=true` keeps admin reachable with the flag intact — use this to embed
 *   joy-web pages inside the planner.
 * - `?feature.useJoyAIPlanner=false` turns the flag itself off for the request (see
 *   `useEventFeatureFlagsContext`), which is the debugging hatch.
 */
export const useJoyAIPlannerRedirect = ({ skip = false }: { skip?: boolean } = {}): void => {
  const [isJoyAIPlannerEnabled] = useIsFeatureEnabled({ eventFeatureFlagKey: 'useJoyAIPlanner', skip });
  const { eventInfo, ssrEventInfo } = useEventInfo();
  // `eventInfo` is undefined during the provider's initial-load phase, where the SSR-seeded id is
  // the only one available — prefer the resolved value once it lands.
  const eventId = eventInfo?.eventId ?? ssrEventInfo?.eventId ?? undefined;

  const { getValueString } = useQueryParamHelper();
  const isOverrideInUrl = getValueString(JOY_PLANNER_REDIRECT_OVERRIDE_PARAM) === 'true';
  // Latched rather than read per-render: react-router drops the param on client-side navigation, so
  // an embedded page would start redirecting the moment the user clicked anything inside the frame.
  // Component state (not a module-level flag) keeps this per-document, so it can't leak across SSR
  // requests on the server.
  const [isRedirectDisabled, setIsRedirectDisabled] = useState(isOverrideInUrl);
  useEffect(() => {
    if (isOverrideInUrl) {
      setIsRedirectDisabled(true);
    }
  }, [isOverrideInUrl]);

  useEffect(() => {
    // `isOverrideInUrl` is checked alongside the latched state, not instead of it. If the param
    // first appears on the same render that the flag or event id resolves, the latch is still false
    // here — the effect above has only queued its update, and this effect runs in the same commit —
    // so relying on state alone would redirect straight past the override.
    if (skip || isRedirectDisabled || isOverrideInUrl || !isJoyAIPlannerEnabled || !eventId) {
      return;
    }
    window.location.assign(buildJoyPlannerEventPath(eventId));
  }, [skip, isRedirectDisabled, isOverrideInUrl, isJoyAIPlannerEnabled, eventId]);
};
